import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { BlogPost } from '@/mocks/blogPosts';
import { blogPosts as mockBlogPosts } from '@/mocks/blogPosts';

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useMock, setUseMock] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('kangstudy_blog_posts')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        if (!cancelled) {
          if (data && data.length > 0) {
            const mapped: BlogPost[] = data.map((item: any) => ({
              id: item.id,
              title: item.title,
              slug: item.slug,
              category: item.category,
              categoryLabel: item.category_label,
              thumbnail: item.thumbnail || '',
              excerpt: item.excerpt || '',
              content: item.content,
              author: item.author,
              authorAvatar: item.author_avatar || '',
              createdAt: item.created_at ? new Date(item.created_at).toISOString().slice(0, 10).replace(/-/g, '.') : '',
              readTime: item.read_time || 3,
              tags: Array.isArray(item.tags) ? item.tags : [],
              featured: item.featured || false,
            }));
            setPosts(mapped);
            setUseMock(false);
          } else {
            // No data in Supabase, fall back to mock
            setPosts(mockBlogPosts);
            setUseMock(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch blog posts from Supabase:', err);
          setPosts(mockBlogPosts);
          setUseMock(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPosts();

    return () => { cancelled = true; };
  }, []);

  return { posts, loading, error, useMock };
}

export function useBlogPost(slug: string) {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchPost() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('kangstudy_blog_posts')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (!cancelled) {
          if (data) {
            const mapped: BlogPost = {
              id: data.id,
              title: data.title,
              slug: data.slug,
              category: data.category,
              categoryLabel: data.category_label,
              thumbnail: data.thumbnail || '',
              excerpt: data.excerpt || '',
              content: data.content,
              author: data.author,
              authorAvatar: data.author_avatar || '',
              createdAt: data.created_at ? new Date(data.created_at).toISOString().slice(0, 10).replace(/-/g, '.') : '',
              readTime: data.read_time || 3,
              tags: Array.isArray(data.tags) ? data.tags : [],
              featured: data.featured || false,
            };
            setPost(mapped);
          } else {
            // Fall back to mock
            const mockPost = mockBlogPosts.find((p) => p.slug === slug) || null;
            setPost(mockPost);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch blog post from Supabase:', err);
          const mockPost = mockBlogPosts.find((p) => p.slug === slug) || null;
          setPost(mockPost);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPost();

    return () => { cancelled = true; };
  }, [slug]);

  return { post, loading, error };
}